import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { setToken, removeToken } from '../utils/auth';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '', remember_me: false });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleGoogleSignIn = () => {
    setGoogleLoading(true);
    // Drop any stale/expired token so the session exchange starts clean
    removeToken();
    // CRITICAL: Use window.location.origin dynamically - no hardcoding or fallbacks
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Restoring this page from the back/forward cache keeps React state, which would
  // leave the Google button stuck in its disabled "Connecting..." state.
  useEffect(() => {
    const resetGoogleButton = () => setGoogleLoading(false);
    window.addEventListener('pageshow', resetGoogleButton);
    return () => window.removeEventListener('pageshow', resetGoogleButton);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      const response = await api.post('/auth/login', formData);
      setToken(response.data.token);
      
      // Check profile completion and verification
      const meRes = await api.get('/auth/me');
      
      if (!meRes.data.is_profile_complete) {
        navigate('/profile-setup');
      } else if (meRes.data.matches_remaining > 0) {
        navigate('/dashboard');
      } else {
        navigate('/paywall');
      }
      
      toast.success('Welcome back!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid credentials';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container gradient-bg">
      <div className="px-4 py-8 min-h-screen flex flex-col">
        {/* Header */}
        <div className="text-center mb-8">
          <Heart className="text-primary fill-primary mx-auto mb-2 pulse-animation" size={40} />
          <h1 className="text-4xl font-bold text-white mb-2">Welcome Back 💜</h1>
          <p className="text-gray-400">Continue your blind dating journey</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 flex-1" autoComplete="on">
          {/* Email */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">Email</label>
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value.toLowerCase().trim()})}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              placeholder="you@example.com"
              required
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
              inputMode="email"
              data-testid="login-email-input"
            />
          </div>

          {/* Password */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block">Password</label>
            <input
              type="password"
              name="password"
              id="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl h-12 px-4 text-white placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              placeholder="Enter your password"
              required
              autoComplete="current-password"
              data-testid="login-password-input"
            />
          </div>

          {/* Remember Me */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="remember_me"
              checked={formData.remember_me}
              onChange={(e) => setFormData({...formData, remember_me: e.target.checked})}
              className="w-5 h-5 rounded bg-secondary/50 border border-white/20 text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer accent-primary"
              data-testid="login-remember-me-checkbox"
            />
            <label htmlFor="remember_me" className="text-gray-400 text-sm cursor-pointer select-none">
              Remember me for 90 days
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="login-submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Logging in...
              </>
            ) : (
              'Log In'
            )}
          </button>
        </form>

        {/* Links */}
        <div className="mt-8 space-y-4 text-center">
          <Link to="/reset-password" className="block text-gray-400 hover:text-white transition-colors" data-testid="login-forgot-password-link">
            Forgot password?
          </Link>
          
          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-white/10"></div>
            <span className="text-gray-500 text-sm">or</span>
            <div className="flex-1 h-px bg-white/10"></div>
          </div>
          
          {/* Google Sign In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="w-full h-12 bg-white hover:bg-gray-100 text-gray-800 font-semibold rounded-full transition-all duration-300 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
            data-testid="google-signin-button"
          >
            {googleLoading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Connecting...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>
          
          <Link to="/signup" className="block text-gray-400 hover:text-white transition-colors" data-testid="login-signup-link">
            Don't have an account? Sign up
          </Link>
        </div>
      </div>
    </div>
  );
}
